import { useCallback, useEffect, useRef, useState } from 'react';
import { queryGeolocationPermission } from '../utils/geolocationPermission';

export type GeolocationModalKind = 'none' | 'soft' | 'denied';

export interface UseGeolocationPermissionFlowResult {
    /** Açık modal: yok, ön-izin (soft), veya reddedildi yardımı */
    modal: GeolocationModalKind;
    /**
     * Konum alma işlemini başlatır. İzin `granted` ise `execute` hemen çağrılır.
     * `prompt` (veya API bilinmiyorsa) soft modal açılır; native izin burada tetiklenmez.
     * `denied` ise yardım modalı açılır.
     * @returns `unsupported` — Geolocation yok; `false` döner ve modal açılmaz.
     */
    begin: (execute: () => void) => Promise<boolean>;
    /** Soft modal: kullanıcı "Konumumu Kullan" dediğinde — native izin burada tetiklenir */
    confirmSoftPrompt: () => void;
    /** Soft modal: "Şimdi değil" — kapat, istek yok */
    dismissSoftPrompt: () => void;
    closeDeniedModal: () => void;
    /** getCurrentPosition PERMISSION_DENIED döndüğünde kuru metin yerine yardım modalı */
    showDeniedHelp: () => void;
}

/**
 * Geolocation izin akışı: Permissions API ile durum kontrolü, soft prompt ve reddedildi yardımı.
 */
export function useGeolocationPermissionFlow(): UseGeolocationPermissionFlowResult {
    const [modal, setModal] = useState<GeolocationModalKind>('none');
    const pendingExecuteRef = useRef<(() => void) | null>(null);

    const clearPending = useCallback(() => {
        pendingExecuteRef.current = null;
    }, []);

    const dismissSoftPrompt = useCallback(() => {
        setModal('none');
        clearPending();
    }, [clearPending]);

    const closeDeniedModal = useCallback(() => {
        setModal('none');
        clearPending();
    }, [clearPending]);

    const showDeniedHelp = useCallback(() => {
        setModal('denied');
        clearPending();
    }, [clearPending]);

    const confirmSoftPrompt = useCallback(() => {
        const fn = pendingExecuteRef.current;
        setModal('none');
        pendingExecuteRef.current = null;
        fn?.();
    }, []);

    const begin = useCallback(async (execute: () => void): Promise<boolean> => {
        if (typeof navigator === 'undefined' || !navigator.geolocation) {
            return false;
        }

        pendingExecuteRef.current = execute;
        const state = await queryGeolocationPermission();

        if (state === 'unsupported') {
            pendingExecuteRef.current = null;
            return false;
        }

        if (state === 'denied') {
            setModal('denied');
            pendingExecuteRef.current = null;
            return true;
        }

        if (state === 'granted') {
            pendingExecuteRef.current = null;
            execute();
            return true;
        }

        setModal('soft');
        return true;
    }, []);

    useEffect(() => {
        if (typeof navigator === 'undefined' || !navigator.permissions?.query) return undefined;

        let status: PermissionStatus | null = null;
        let cancelled = false;

        const onChange = () => {
            if (status?.state === 'granted') {
                setModal((m) => (m === 'denied' ? 'none' : m));
            }
        };

        void (async () => {
            try {
                const s = await navigator.permissions.query({ name: 'geolocation' as PermissionName });
                if (cancelled) return;
                status = s;
                s.addEventListener('change', onChange);
            } catch {
                /* ignore */
            }
        })();

        return () => {
            cancelled = true;
            status?.removeEventListener('change', onChange);
        };
    }, []);

    return {
        modal,
        begin,
        confirmSoftPrompt,
        dismissSoftPrompt,
        closeDeniedModal,
        showDeniedHelp,
    };
}
